import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { can, isTenantAdmin } from '../lib/access';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  Camera, ChevronDown, ChevronsDownUp, ChevronsUpDown, Edit, Heart, Link, Mail, MapPin, Maximize2, Minus,
  Palette, Phone, Plus, RefreshCw, Sparkles, User, UserMinus, Users, X,
} from 'lucide-react';
import InfoTip from '../components/InfoTip';
import AuthImage from '../components/AuthImage';
import RoleDrawer from '../components/org/RoleDrawer';
import PersonPicker from '../components/org/PersonPicker';
import {
  DEFAULT_ORG_LEVELS, apiError, buildTree, descendantIds, initials, isVacant, levelChipStyle, levelMeta, placementOf, rolesByUser,
} from '../lib/orgStructure';

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.5;
const DRAG_TYPE = 'text/org-position-id';

const Avatar = ({ person, size = 40 }) => <span className="grid shrink-0 place-items-center overflow-hidden rounded-full bg-slate-100 font-bold text-slate-500" style={{ width: size, height: size, fontSize: size / 3 }}>
  <AuthImage src={person?.photo_url} className="h-full w-full object-cover" fallback={person ? initials(person.name) : <User size={size / 2.4} aria-hidden="true" />} />
</span>;

const LevelSettingsDialog = ({ open, onOpenChange, levels, onSaved }) => {
  const [draft, setDraft] = useState(levels);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDraft(levels.map((level) => ({ ...level }))); }, [open, levels]);
  const update = (index, key, value) => setDraft((current) => current.map((level, i) => (i === index ? { ...level, [key]: value } : level)));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put('/org-levels', draft.map(({ label, color }) => ({ label, color })));
      toast.success('Levels saved');
      onSaved(data);
      onOpenChange(false);
    } catch (error) { toast.error(apiError(error, 'Could not save levels')); }
    finally { setSaving(false); }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-md">
    <DialogHeader><DialogTitle>Role levels</DialogTitle><DialogDescription>Names and colours used on the Org Chart and Roles table for this organisation. Top level first.</DialogDescription></DialogHeader>
    <div className="space-y-2">
      {draft.map((level, index) => <div key={index} className="flex items-center gap-2">
        <span className="w-6 text-right text-xs text-slate-500">{index + 1}</span>
        <label className="sr-only" htmlFor={`level-color-${index}`}>Colour for {level.label}</label>
        <input id={`level-color-${index}`} type="color" value={level.color} onChange={(event) => update(index, 'color', event.target.value)} className="h-9 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5" />
        <label className="sr-only" htmlFor={`level-label-${index}`}>Level name</label>
        <Input id={`level-label-${index}`} value={level.label} onChange={(event) => update(index, 'label', event.target.value)} className="h-9" />
      </div>)}
    </div>
    <div className="flex items-center justify-between pt-2">
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" disabled={draft.length >= 12} onClick={() => setDraft((current) => [...current, { label: 'New level', color: '#8a90a0' }])}><Plus size={14} className="mr-1" />Add level</Button>
        <Button type="button" variant="outline" size="sm" disabled={draft.length <= 1} onClick={() => setDraft((current) => current.slice(0, -1))}><Minus size={14} className="mr-1" />Remove last</Button>
      </div>
      <Button onClick={save} disabled={saving} className="qflow-accent-bg hover:opacity-90">Save levels</Button>
    </div>
  </DialogContent></Dialog>;
};

const OrgChartView = ({ user }) => {
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [levels, setLevels] = useState(DEFAULT_ORG_LEVELS);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [zoom, setZoom] = useState(1);
  const [dropTarget, setDropTarget] = useState(null);
  const [roleDrawer, setRoleDrawer] = useState({ open: false, role: null, defaults: null });
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ phone: '', hobby: '', fun_fact: '', location: '' });
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  // Editing the chart is the "Manage teams and users" tick; other people's profiles stay with the Tenant Admin.
  const isAdmin = can(user, 'can_manage_teams');

  const fetchData = useCallback(async () => {
    try {
      const [positionsRes, usersRes, levelsRes, treeRes] = await Promise.all([
        axiosInstance.get('/org-positions'),
        axiosInstance.get('/users'),
        axiosInstance.get('/org-levels').catch(() => ({ data: DEFAULT_ORG_LEVELS })),
        axiosInstance.get('/categories/tree').catch(() => ({ data: { tree: [] } })),
      ]);
      setPositions(positionsRes.data || []);
      setUsers(usersRes.data || []);
      setLevels(levelsRes.data?.length ? levelsRes.data : DEFAULT_ORG_LEVELS);
      const teamList = [];
      (treeRes.data?.tree || []).forEach((sector) => (sector.departments || []).forEach((department) =>
        (department.teams || []).forEach((team) => teamList.push({ ...team, path: `${sector.name} / ${department.name}` }))));
      setTeams(teamList.sort((left, right) => left.name.localeCompare(right.name)));
    } catch (error) {
      toast.error('Failed to load organization chart');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tree = useMemo(() => buildTree(positions), [positions]);
  const byId = useMemo(() => Object.fromEntries(positions.map((position) => [position.id, position])), [positions]);
  const unplaced = useMemo(() => positions.filter((position) => placementOf(position) === 'unplaced')
    .sort((left, right) => (left.level || 0) - (right.level || 0) || (left.title || '').localeCompare(right.title || '')), [positions]);
  const selected = selectedId ? byId[selectedId] : null;
  const selectedReports = useMemo(() => (selected ? positions.filter((position) => position.parent_id === selected.id) : []), [positions, selected]);
  const roleCounts = useMemo(() => Object.fromEntries(Object.entries(rolesByUser(positions)).map(([id, list]) => [id, list.length])), [positions]);

  const refreshAfter = async (request, success, failure) => {
    try { await request(); if (success) toast.success(success); await fetchData(); }
    catch (error) { toast.error(apiError(error, failure)); }
  };

  const handleSyncTeams = async () => {
    setSyncing(true);
    try {
      const res = await axiosInstance.post('/org-positions/sync-teams');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync teams');
    } finally {
      setSyncing(false);
    }
  };

  const handleReparent = (positionId, parentId) => {
    if (!positionId || positionId === parentId) return;
    if (byId[positionId]?.parent_id === parentId) return;
    if (descendantIds(positions, positionId).has(parentId)) { toast.error('A role cannot report to one of its own reports'); return; }
    refreshAfter(() => axiosInstance.put(`/org-positions/${positionId}`, { parent_id: parentId }), 'Reporting line updated', 'Unable to move this role');
  };
  const handleUnplace = (positionId) => {
    if (!positionId || placementOf(byId[positionId]) === 'unplaced') return;
    refreshAfter(() => axiosInstance.put(`/org-positions/${positionId}`, { parent_id: null, placement: 'unplaced' }), 'Moved to Unplaced roles', 'Unable to move this role');
  };
  const handleAssign = (positionId, userId) => {
    setAssignOpen(false);
    refreshAfter(() => axiosInstance.put(`/org-positions/${positionId}`, { user_id: userId || null }), userId ? 'Role filled' : 'Role marked vacant', 'Failed to update role');
  };

  const handleUpdateProfile = async () => {
    if (!selected?.user?.id) return;
    try {
      await axiosInstance.patch(`/users/${selected.user.id}/profile`, profileForm);
      toast.success('Profile updated successfully');
      setIsProfileDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected?.user?.id) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axiosInstance.post(`/users/${selected.user.id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const openProfileDialog = () => {
    setProfileForm({
      phone: selected?.user?.phone || '',
      hobby: selected?.user?.hobby || '',
      fun_fact: selected?.user?.fun_fact || '',
      location: selected?.user?.location || '',
    });
    setIsProfileDialogOpen(true);
  };

  const setZoomClamped = (value) => setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 100) / 100)));
  const centerChart = () => requestAnimationFrame(() => {
    const canvas = canvasRef.current;
    if (canvas) canvas.scrollLeft = Math.max(0, (canvas.scrollWidth - canvas.clientWidth) / 2);
  });
  const fitToScreen = () => {
    const canvas = canvasRef.current;
    const chart = chartRef.current?.firstElementChild;
    if (!canvas || !chart) return;
    // The chart is drawn at the current zoom, so undo it to get its natural size.
    const naturalWidth = chart.getBoundingClientRect().width / zoom + 64;
    const naturalHeight = chart.getBoundingClientRect().height / zoom + 96;
    setZoomClamped(Math.min(1, canvas.clientWidth / naturalWidth, canvas.clientHeight / naturalHeight));
    centerChart();
  };
  const fittedOnce = useRef(false);
  useEffect(() => {
    if (loading || fittedOnce.current || !tree.length) return;
    fittedOnce.current = true;
    requestAnimationFrame(fitToScreen);
  });
  const expandAll = () => setCollapsed({});
  const collapseAll = () => {
    const next = {};
    tree.forEach((root) => root.children.forEach(function walk(node) {
      if (node.children.length) next[node.id] = true;
      node.children.forEach(walk);
    }));
    setCollapsed(next);
  };

  const dragProps = (positionId) => (isAdmin ? {
    draggable: true,
    onDragStart: (event) => { event.stopPropagation(); event.dataTransfer.setData(DRAG_TYPE, positionId); event.dataTransfer.effectAllowed = 'move'; },
    onDragEnd: () => setDropTarget(null),
  } : {});
  const dropProps = (targetId, onDropId) => (isAdmin ? {
    onDragOver: (event) => { event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = 'move'; if (dropTarget !== targetId) setDropTarget(targetId); },
    onDragLeave: (event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDropTarget((current) => (current === targetId ? null : current)); },
    onDrop: (event) => { event.preventDefault(); event.stopPropagation(); setDropTarget(null); onDropId(event.dataTransfer.getData(DRAG_TYPE)); },
  } : {});

  const renderNode = (node) => {
    const level = levelMeta(levels, node.level);
    const vacant = isVacant(node);
    const isCollapsed = Boolean(collapsed[node.id]);
    return <li key={node.id}>
      <div className="relative">
        <button type="button" className="qflow-org-node block text-left" aria-pressed={selectedId === node.id} data-vacant={vacant} data-drop={dropTarget === node.id}
          onClick={() => setSelectedId(node.id)} {...dragProps(node.id)} {...dropProps(node.id, (movedId) => handleReparent(movedId, node.id))}
          aria-label={`${node.title}, ${vacant ? 'vacant' : node.user?.name}`} data-testid={`org-node-${node.id}`}>
          <span className="block h-1 rounded-t-[11px]" style={{ background: level.color }} aria-hidden="true" />
          <span className="flex items-center gap-2.5 px-3 py-2.5">
            <Avatar person={vacant ? null : node.user} size={36} />
            <span className="min-w-0 flex-1">
              <span className={`block truncate text-sm font-bold ${vacant ? 'text-slate-500' : ''}`}>{vacant ? 'Vacant' : node.user?.name}</span>
              <span className="block truncate text-xs text-slate-600">{node.title}</span>
            </span>
          </span>
          <span className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-1.5 text-[11px]">
            <span className="whitespace-nowrap rounded px-1.5 py-0.5 font-bold" style={levelChipStyle(level.color)}>{level.label}</span>
            {node.department && <span className="flex min-w-0 items-center gap-1 truncate text-slate-500"><Users size={11} aria-hidden="true" />{node.department}</span>}
          </span>
        </button>
        {node.children.length > 0 && <button type="button" onClick={() => setCollapsed((current) => ({ ...current, [node.id]: !current[node.id] }))}
          aria-expanded={!isCollapsed} aria-label={`${isCollapsed ? 'Show' : 'Hide'} ${node.children.length} report${node.children.length === 1 ? '' : 's'} of ${node.title}`}
          className="absolute -bottom-3 left-1/2 z-10 flex h-6 min-w-6 -translate-x-1/2 items-center justify-center gap-0.5 rounded-full border border-slate-200 bg-white px-1.5 text-[11px] font-bold text-slate-600 shadow-sm hover:text-slate-900">
          {isCollapsed ? node.children.length : <ChevronDown size={12} />}
        </button>}
      </div>
      {node.children.length > 0 && !isCollapsed && <ul>{node.children.map(renderNode)}</ul>}
    </li>;
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" /></div>;
  }

  const selectedLevel = selected ? levelMeta(levels, selected.level) : null;
  const selectedParent = selected?.parent_id ? byId[selected.parent_id] : null;
  const selectedTeam = selected?.team_id ? teams.find((team) => team.id === selected.team_id) : null;
  const canEditProfile = selected?.user && (isTenantAdmin(user) || selected.user.id === user.id);

  return <div className="min-h-screen space-y-4 bg-slate-50 p-4 lg:p-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold text-slate-900">Org Chart</h1>
        <InfoTip label="About the Org Chart">Every box is a role from Teams & Users. Drag a role onto another to change who it reports to, or onto Unplaced roles to take it off the chart.</InfoTip>
      </div>
      {isAdmin && <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={handleSyncTeams} disabled={syncing}>{syncing ? <RefreshCw size={15} className="mr-2 animate-spin" /> : <Link size={15} className="mr-2" />}Sync from Teams</Button>
        <Button variant="outline" onClick={() => setLevelsOpen(true)}><Palette size={15} className="mr-2" />Levels</Button>
        <Button onClick={() => setRoleDrawer({ open: true, role: null, defaults: null })} className="bg-blue-600 hover:bg-blue-700" data-testid="org-new-role"><Plus size={15} className="mr-2" />New role</Button>
      </div>}
    </div>

    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center rounded-md border border-slate-200 bg-white" role="group" aria-label="Zoom">
        <button type="button" className="grid h-9 w-9 place-items-center text-slate-600 hover:text-slate-900 disabled:opacity-40" onClick={() => setZoomClamped(zoom - 0.1)} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out"><Minus size={15} /></button>
        <button type="button" className="h-9 min-w-[3.5rem] border-x border-slate-200 px-2 text-xs font-semibold text-slate-700" onClick={() => setZoom(1)} aria-label="Reset zoom to 100%">{Math.round(zoom * 100)}%</button>
        <button type="button" className="grid h-9 w-9 place-items-center text-slate-600 hover:text-slate-900 disabled:opacity-40" onClick={() => setZoomClamped(zoom + 0.1)} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in"><Plus size={15} /></button>
      </div>
      <Button variant="outline" size="sm" className="h-9" onClick={fitToScreen}><Maximize2 size={14} className="mr-1.5" />Fit</Button>
      <Button variant="outline" size="sm" className="h-9" onClick={expandAll}><ChevronsUpDown size={14} className="mr-1.5" />Expand all</Button>
      <Button variant="outline" size="sm" className="h-9" onClick={collapseAll}><ChevronsDownUp size={14} className="mr-1.5" />Collapse all</Button>
      <div className="ml-auto flex flex-wrap items-center gap-1.5 text-xs" aria-label="Levels">
        {levels.map((level) => <span key={level.rank} className="whitespace-nowrap rounded px-1.5 py-0.5 font-bold" style={levelChipStyle(level.color)}>{level.label}</span>)}
      </div>
    </div>

    <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
      <div ref={canvasRef} className="relative min-h-[520px] min-w-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white" style={{ maxHeight: 'calc(100vh - 220px)' }} data-testid="org-canvas">
        {tree.length ? <div ref={chartRef} className="qflow-org inline-block min-w-full p-8 pb-12" style={{ zoom }}>
          <ul>{tree.map(renderNode)}</ul>
        </div> : <div className="flex h-[520px] flex-col items-center justify-center gap-3 p-8 text-center">
          <Users size={40} className="text-slate-300" aria-hidden="true" />
          <p className="font-semibold text-slate-700">No roles on the chart yet</p>
          <p className="max-w-sm text-sm text-slate-500">Create a top-level role, then drag roles from Unplaced roles under it.</p>
          {isAdmin && <Button onClick={() => setRoleDrawer({ open: true, role: null, defaults: { level: 0 } })} className="bg-blue-600 hover:bg-blue-700"><Plus size={15} className="mr-2" />New role</Button>}
        </div>}
      </div>

      <aside className="grid w-full shrink-0 gap-4 md:grid-cols-2 xl:block xl:w-80 xl:space-y-4">
        {selected && <section className="rounded-xl border border-slate-200 bg-white" aria-labelledby="org-panel-title" data-testid="org-side-panel">
          <div className="flex items-start gap-3 border-b border-slate-100 p-4">
            <div className="relative">
              <Avatar person={isVacant(selected) ? null : selected.user} size={52} />
              {canEditProfile && <label className="absolute -bottom-1 -right-1 grid h-6 w-6 cursor-pointer place-items-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:text-slate-900" title="Upload photo">
                <Camera size={12} aria-hidden="true" /><span className="sr-only">Upload photo</span>
                <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} />
              </label>}
            </div>
            <div className="min-w-0 flex-1">
              <h2 id="org-panel-title" className="truncate text-base font-bold text-slate-900">{selected.user?.name || 'Vacant role'}</h2>
              <p className="truncate text-sm text-slate-600">{selected.title}</p>
              <span className="mt-1 inline-block rounded px-1.5 py-0.5 text-[11px] font-bold" style={levelChipStyle(selectedLevel.color)}>{selectedLevel.label}</span>
            </div>
            <button type="button" onClick={() => setSelectedId(null)} className="grid h-8 w-8 place-items-center rounded-md text-slate-500 hover:bg-slate-100" aria-label="Close details"><X size={16} /></button>
          </div>
          <dl className="space-y-2 p-4 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Reports to</dt><dd className="text-right">{selectedParent ? <button type="button" className="font-semibold text-blue-600 hover:underline" onClick={() => setSelectedId(selectedParent.id)}>{selectedParent.title}</button> : placementOf(selected) === 'root' ? 'Top of chart' : <span className="font-semibold text-amber-700">Not placed</span>}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Team</dt><dd className="text-right">{selectedTeam?.name || selected.department || 'None'}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Status</dt><dd className={`font-semibold ${isVacant(selected) ? 'text-amber-700' : 'text-emerald-700'}`}>{isVacant(selected) ? 'Vacant' : 'Filled'}</dd></div>
            {selected.user?.email && <div className="flex items-center gap-2 pt-1 text-slate-700"><Mail size={14} className="text-slate-400" aria-hidden="true" /><span className="truncate">{selected.user.email}</span></div>}
            {selected.user?.phone && <div className="flex items-center gap-2 text-slate-700"><Phone size={14} className="text-slate-400" aria-hidden="true" />{selected.user.phone}</div>}
            {selected.user?.location && <div className="flex items-center gap-2 text-slate-700"><MapPin size={14} className="text-slate-400" aria-hidden="true" />{selected.user.location}</div>}
            {selected.user?.hobby && <div className="flex items-start gap-2 text-slate-700"><Heart size={14} className="mt-0.5 text-slate-400" aria-hidden="true" />{selected.user.hobby}</div>}
            {selected.user?.fun_fact && <div className="flex items-start gap-2 italic text-slate-700"><Sparkles size={14} className="mt-0.5 text-slate-400" aria-hidden="true" />{selected.user.fun_fact}</div>}
          </dl>
          {selectedReports.length > 0 && <div className="border-t border-slate-100 p-4">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Direct reports ({selectedReports.length})</h3>
            <ul className="max-h-44 space-y-1 overflow-y-auto">{selectedReports.map((child) => <li key={child.id}>
              <button type="button" onClick={() => setSelectedId(child.id)} className="flex w-full items-center gap-2 rounded-md p-1.5 text-left hover:bg-slate-50">
                <Avatar person={isVacant(child) ? null : child.user} size={28} />
                <span className="min-w-0"><span className="block truncate text-sm font-semibold">{child.user?.name || 'Vacant'}</span><span className="block truncate text-xs text-slate-500">{child.title}</span></span>
              </button>
            </li>)}</ul>
          </div>}
          {(isAdmin || canEditProfile) && <div className="grid gap-2 border-t border-slate-100 p-4">
            {isAdmin && <Popover open={assignOpen} onOpenChange={setAssignOpen}>
              <PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><User size={14} className="mr-2" />{isVacant(selected) ? 'Assign person' : 'Change person'}</Button></PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-2"><PersonPicker autoFocus people={users} value={selected.user_id || ''} allowVacant={false} roleCounts={roleCounts} onChange={(id) => handleAssign(selected.id, id)} /></PopoverContent>
            </Popover>}
            {isAdmin && !isVacant(selected) && <Button variant="outline" className="w-full justify-start" onClick={() => handleAssign(selected.id, null)}><UserMinus size={14} className="mr-2" />Make vacant</Button>}
            {isAdmin && <Button variant="outline" className="w-full justify-start" onClick={() => setRoleDrawer({ open: true, role: null, defaults: { parent_id: selected.id, level: Math.min((selected.level || 0) + 1, levels.length - 1), team_id: selected.team_id } })}><Plus size={14} className="mr-2" />Add direct report</Button>}
            {isAdmin && <Button variant="outline" className="w-full justify-start" onClick={() => setRoleDrawer({ open: true, role: selected, defaults: null })}><Edit size={14} className="mr-2" />Edit role</Button>}
            {canEditProfile && <Button variant="outline" className="w-full justify-start" onClick={openProfileDialog}><User size={14} className="mr-2" />Edit profile</Button>}
          </div>}
        </section>}

        <section className="qflow-org-drop rounded-xl border border-dashed border-slate-300 bg-white" data-drop={dropTarget === 'tray'} aria-labelledby="unplaced-title" data-testid="unplaced-tray" {...dropProps('tray', handleUnplace)}>
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h2 id="unplaced-title" className="text-sm font-bold text-slate-900">Unplaced roles <span className="font-semibold text-slate-500">{unplaced.length}</span></h2>
            <InfoTip label="About unplaced roles">Roles that do not report to anyone yet. Drag one onto a role on the chart to place it there.</InfoTip>
          </div>
          {unplaced.length ? <ul className="max-h-72 space-y-1 overflow-y-auto p-2">{unplaced.map((position) => {
            const level = levelMeta(levels, position.level);
            return <li key={position.id}>
              <button type="button" {...dragProps(position.id)} onClick={() => setSelectedId(position.id)} className={`flex w-full items-center gap-2 rounded-md border border-transparent p-2 text-left hover:border-slate-200 hover:bg-slate-50 ${isAdmin ? 'cursor-grab' : ''} ${selectedId === position.id ? 'border-blue-200 bg-blue-50' : ''}`}>
                <span className="h-8 w-1 shrink-0 rounded-full" style={{ background: level.color }} aria-hidden="true" />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{position.title}</span><span className="block truncate text-xs text-slate-500">{position.user?.name || 'Vacant'} · {level.label}</span></span>
              </button>
            </li>;
          })}</ul> : <p className="px-4 py-6 text-center text-sm text-slate-500">{isAdmin ? 'Every role is on the chart. Drag a role here to take it off.' : 'Every role is on the chart.'}</p>}
        </section>
      </aside>
    </div>

    <RoleDrawer open={roleDrawer.open} onOpenChange={(open) => setRoleDrawer((current) => ({ ...current, open }))} role={roleDrawer.role} defaults={roleDrawer.defaults}
      positions={positions} people={users} teams={teams} levels={levels} onSaved={fetchData} />
    <LevelSettingsDialog open={levelsOpen} onOpenChange={setLevelsOpen} levels={levels} onSaved={setLevels} />

    <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit profile</DialogTitle></DialogHeader>
        <div className="mt-2 space-y-4">
          <div><Label className="flex items-center gap-1.5"><Phone size={14} /> Phone</Label><Input value={profileForm.phone} onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+1 (555) 123-4567" /></div>
          <div><Label className="flex items-center gap-1.5"><MapPin size={14} /> Location</Label><Input value={profileForm.location} onChange={(e) => setProfileForm((p) => ({ ...p, location: e.target.value }))} placeholder="New York, NY" /></div>
          <div><Label className="flex items-center gap-1.5"><Heart size={14} /> Hobbies & Interests</Label><Textarea value={profileForm.hobby} onChange={(e) => setProfileForm((p) => ({ ...p, hobby: e.target.value }))} placeholder="What do you enjoy doing outside of work?" rows={2} /></div>
          <div><Label className="flex items-center gap-1.5"><Sparkles size={14} /> Something About Me</Label><Textarea value={profileForm.fun_fact} onChange={(e) => setProfileForm((p) => ({ ...p, fun_fact: e.target.value }))} placeholder="Share something interesting about yourself!" rows={2} /></div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setIsProfileDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateProfile}>Save profile</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>;
};

export default OrgChartView;
